/**
 * AuthGate — the boot guard at the root of authenticated routes. On mount it
 * attempts a silent refresh; on success it renders the protected shell and, if a
 * deep link was stashed before a prior login redirect, navigates there. On
 * failure it stashes the current path and redirects the browser to centralized
 * login.
 *
 * Mounting the gate at the root means a deep-link load lands the user back where
 * they wanted to go after sign-in.
 *
 * **Role gate (DNKADM-0351; retargeted DNKADM-0359):** once authenticated, a
 * signed-in principal who is not an *operator* (`operator.isOperator` — OWNER,
 * SUPER_ADMIN, ADMIN or STAFF) never renders the shell at all: it renders
 * `NoConsoleAccess` instead of the `<Outlet/>`, above every route, before the
 * AppLayout nav/shell mounts. A customer (`tenant_role: "USER"`) still lands
 * here; a STAFF member no longer does.
 *
 * This gate asks *may you enter*; `RequireAdmin` further down asks *may you
 * configure* (`operator.isAdmin`, still OWNER/ADMIN/SUPER_ADMIN only). Until
 * DNKADM-0359 those were the same boolean, which is exactly why a STAFF token
 * got a locked app. Separating them opens nothing that was admin-only. The
 * `auth/*` routes (return, error, signed-out, session-expired) are registered as
 * siblings of this gate in `router.tsx`, not children of it, so they stay
 * reachable for a non-admin regardless of this check.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { bootRefresh, popDeepLink, redirectToCentralizedLogin } from './silentRefresh';
import { getAccessToken } from './authStore';
import { useAuth } from './authHooks';
import { SessionExpired } from '@/routes/SessionExpired';
import { NoConsoleAccess } from '@/routes/NoConsoleAccess';

type GateState = 'booting' | 'authed' | 'redirecting';

export function AuthGate(): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthed, sessionExpired, operator } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (getAccessToken()) {
      setState('authed');
      return;
    }

    void (async () => {
      const ok = await bootRefresh();
      if (ok) {
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        setState('authed');
        // Restore a stashed deep link (set before the centralized-login round-trip).
        const deep = popDeepLink();
        if (deep && deep !== location.pathname + location.search) {
          navigate(deep, { replace: true });
        }
      } else {
        setState('redirecting');
        redirectToCentralizedLogin(location.pathname + location.search);
      }
    })();
  }, [location.pathname, location.search, navigate, setAuthed]);

  // Runtime session-expiry (a bearer-surface 401 that couldn't be refreshed) flips
  // the AuthContext flag from the axios layer; every authenticated screen mounts
  // under this gate's <Outlet/>, so rendering the interstitial here makes it global
  // with no navigation from non-React code. The boot branches below are untouched.
  if (sessionExpired) {
    return <SessionExpired />;
  }

  if (state === 'authed') {
    // A principal who is not an operator never renders the shell — checked once
    // here, above every route, rather than per-route (DNKADM-0351/0359).
    // `RequireAdmin` further down still separately gates Configuration for a
    // non-admin operator, which is now a real case (STAFF).
    if (operator?.isOperator !== true) {
      return <NoConsoleAccess />;
    }
    return <Outlet />;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '0.5rem',
      }}
    >
      <p>{state === 'redirecting' ? 'Redirecting to sign in…' : 'Loading…'}</p>
    </div>
  );
}
